import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faKey,
  faRotate,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { ShareboxLockerModel, ShareboxMatchStatus } from 'api/model';
import { useShareboxGetHubOverview } from 'api/sharebox/sharebox';
import useHasAccessToSharebox from 'hooks/useHasAccessToSharebox';
import { FC } from 'react';
import styled from 'styled-components';
import { formatDayMonth, formatTime } from 'utils/date-helpers';

const Page = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 20px;
`;

const Explainer = styled.p`
  margin: 0;
  max-width: 780px;
  color: ${({ theme }) => theme.colors.foreground.secondary};
`;

const HubSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const HubHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
`;

const HubMeta = styled.span`
  font-weight: 400;
  color: ${({ theme }) => theme.colors.foreground.secondary};
`;

const WarningStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: 3px;
  background: ${({ theme }) => theme.colors.background.highlight};
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    text-align: left;
    padding: 6px 10px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
    vertical-align: top;
  }

  th {
    white-space: nowrap;
    color: ${({ theme }) => theme.colors.foreground.secondary};
    font-weight: 600;
  }
`;

/** Rader med misstänkt felmappning lyfts fram — det är hela anledningen till vyn. */
const Row = styled.tr<{ $suspect: boolean }>`
  background: ${({ $suspect, theme }) =>
    $suspect ? theme.colors.background.highlight : 'transparent'};
`;

const Mono = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  word-break: break-word;
`;

const Muted = styled.span`
  color: ${({ theme }) => theme.colors.foreground.secondary};
`;

const matchStatusLabels: Record<ShareboxMatchStatus, string> = {
  [ShareboxMatchStatus.Unmatched]: 'Omatchad',
  [ShareboxMatchStatus.Matched]: 'Matchad',
  [ShareboxMatchStatus.Emptied]: 'Tömd',
};

/**
 * En lucka är misstänkt när Sharebox och spegeln inte är överens, när Sharebox känner
 * till en upptagen lucka vi aldrig speglat, eller när regnr-parsningen gav tomt trots
 * att det ligger en nyckel där.
 */
function isSuspect(locker: ShareboxLockerModel): boolean {
  if (locker.contentDiffersFromMirror) return true;
  if (locker.isOccupied && !locker.inMirror) return true;
  if (locker.isOccupied && !locker.mirrorRegistrationNumber) return true;
  return false;
}

function suspectReason(locker: ShareboxLockerModel): string | null {
  if (locker.isOccupied && !locker.inMirror) {
    return 'Sharebox har en nyckel här som saknas i spegeln — synken har inte hunnit se den.';
  }
  if (locker.contentDiffersFromMirror && !locker.isOccupied) {
    return 'Luckan är tom hos Sharebox men spegeln tror att nyckeln ligger kvar — missad tömning.';
  }
  if (locker.contentDiffersFromMirror) {
    return 'Sharebox text skiljer sig från den vi lagrade.';
  }
  if (locker.isOccupied && !locker.mirrorRegistrationNumber) {
    return 'Regnr-parsningen fick inget giltigt registreringsnummer ur texten.';
  }
  return null;
}

const ShareboxHubOverviewPage: FC = () => {
  const hasAccess = useHasAccessToSharebox();
  const overviewCall = useShareboxGetHubOverview({
    query: { enabled: hasAccess },
  });

  if (!hasAccess) {
    return (
      <Page>
        <Explainer>
          Sharebox-översikten är begränsad till piloten. Be en administratör
          lägga till dig i &quot;Användare i Sharebox-piloten&quot; under
          globala inställningar.
        </Explainer>
      </Page>
    );
  }

  const hubs = overviewCall.data ?? [];

  return (
    <Page>
      <PageHeader>
        <Title>
          <FontAwesomeIcon icon={faKey} /> Sharebox — hubböversikt
        </Title>
        {overviewCall.isFetching && (
          <Muted>
            <FontAwesomeIcon icon={faRotate} spin /> Hämtar från Sharebox…
          </Muted>
        )}
      </PageHeader>

      <Explainer>
        Läses direkt från Sharebox vid varje sidladdning, inte från vår spegel.
        Kolumnen <strong>Sharebox säger</strong> är den råa texten i luckan;{' '}
        <strong>Vi tolkade</strong> är regnumret vår parsning fick ut. Skiljer
        de sig åt är raden markerad.
      </Explainer>

      {overviewCall.isError && (
        <WarningStrip>
          <FontAwesomeIcon icon={faTriangleExclamation} />
          Kunde inte hämta översikten.
        </WarningStrip>
      )}

      {!overviewCall.isLoading && hubs.length === 0 && (
        <Explainer>
          Inga hubbar är kopplade till Sharebox. Sätt &quot;Sharebox
          location-ID&quot; på ett företag i adressboken.
        </Explainer>
      )}

      {hubs.map((hub) => (
        <HubSection key={hub.companyID} id={`hub-${hub.companyID}`}>
          <HubHeader>
            {hub.hubName}
            <HubMeta>
              location {hub.shareboxLocationID} · {hub.lockers.length} luckor ·{' '}
              {hub.lockers.filter((l) => l.isOccupied).length} upptagna
            </HubMeta>
          </HubHeader>

          {hub.liveFetchFailed && (
            <WarningStrip>
              <FontAwesomeIcon icon={faTriangleExclamation} />
              Kunde inte nå Sharebox för den här hubben. Raderna nedan kommer
              enbart från vår spegel och kan vara inaktuella.
            </WarningStrip>
          )}

          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <th>Lucka</th>
                  <th>Skåp</th>
                  <th>Status</th>
                  <th>Sharebox säger</th>
                  <th>Vi tolkade</th>
                  <th>Matchning</th>
                  <th>Ärende</th>
                  <th>Inlagd</th>
                </tr>
              </thead>
              <tbody>
                {hub.lockers.map((locker) => {
                  const suspect = isSuspect(locker);
                  const reason = suspectReason(locker);
                  return (
                    <Row
                      key={`${locker.cabinetSerialNo}-${locker.lockerNo}`}
                      $suspect={suspect}
                      title={reason ?? undefined}
                    >
                      <td>{locker.lockerNo}</td>
                      <td>
                        <Muted>{locker.cabinetSerialNo}</Muted>
                      </td>
                      <td>
                        {locker.isOccupied ? (
                          <>
                            <FontAwesomeIcon icon={faKey} /> Upptagen
                          </>
                        ) : (
                          <Muted>Ledig</Muted>
                        )}
                        {suspect && (
                          <>
                            {' '}
                            <FontAwesomeIcon icon={faTriangleExclamation} />
                          </>
                        )}
                      </td>
                      <td>
                        <Mono>{locker.liveContent || <Muted>—</Muted>}</Mono>
                        {locker.contentDiffersFromMirror &&
                          locker.mirrorRawContent && (
                            <div>
                              <Muted>
                                spegel: <Mono>{locker.mirrorRawContent}</Mono>
                              </Muted>
                            </div>
                          )}
                      </td>
                      <td>
                        {locker.mirrorRegistrationNumber ? (
                          <Mono>{locker.mirrorRegistrationNumber}</Mono>
                        ) : (
                          <Muted>—</Muted>
                        )}
                      </td>
                      <td>
                        {locker.mirrorMatchStatus != null ? (
                          matchStatusLabels[locker.mirrorMatchStatus]
                        ) : (
                          <Muted>ej speglad</Muted>
                        )}
                      </td>
                      <td>
                        {locker.matchedCaseID ? (
                          <a href={`/arenden/${locker.matchedCaseID}`}>
                            {locker.matchedCaseID}
                          </a>
                        ) : (
                          <Muted>—</Muted>
                        )}
                      </td>
                      <td>
                        {locker.keyDroppedInAt ? (
                          <Muted>
                            {formatDayMonth(locker.keyDroppedInAt)}{' '}
                            {formatTime(locker.keyDroppedInAt)}
                          </Muted>
                        ) : (
                          <Muted>—</Muted>
                        )}
                      </td>
                    </Row>
                  );
                })}
              </tbody>
            </Table>
          </TableScroll>
        </HubSection>
      ))}
    </Page>
  );
};

export default ShareboxHubOverviewPage;
